import { useState, useRef, useCallback } from "react";
import { Download, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Reusable Download PDF button for presentation decks.
 *
 * Props:
 *   slides       - array of slide objects
 *   renderSlide  - function(slide, index) -> JSX rendering that slide
 *   filename     - output filename (without .pdf)
 *   bgColor      - background color for hidden export frames (default #0a1628)
 *   slideWidth   - export frame width in px (default 1920)
 *   slideHeight  - export frame height in px (default 1080)
 *   className    - optional override for button styling
 */
export default function PresentationDownloadButton({
  slides,
  renderSlide,
  filename = "Presentation",
  bgColor = "#0a1628",
  slideWidth = 1920,
  slideHeight = 1080,
  className = "flex items-center gap-2 bg-white/5 hover:bg-white/10 disabled:bg-white/5 border border-white/15 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:cursor-not-allowed"
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const exportRef = useRef(null);

  const handleDownload = useCallback(async () => {
    if (isDownloading || !exportRef.current) return;
    setIsDownloading(true);
    setProgress(0);
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [slideWidth, slideHeight] });
      const slideEls = exportRef.current.querySelectorAll("[data-export-slide]");
      for (let i = 0; i < slideEls.length; i++) {
        const canvas = await html2canvas(slideEls[i], {
          backgroundColor: bgColor,
          scale: 1,
          useCORS: true,
          logging: false,
          width: slideWidth,
          height: slideHeight,
          windowWidth: slideWidth,
          windowHeight: slideHeight
        });
        const img = canvas.toDataURL("image/jpeg", 0.92);
        if (i > 0) pdf.addPage([slideWidth, slideHeight], "landscape");
        pdf.addImage(img, "JPEG", 0, 0, slideWidth, slideHeight);
        setProgress(Math.round(((i + 1) / slideEls.length) * 100));
      }
      pdf.save(`${filename}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      alert("Sorry — PDF export failed. Check the console for details.");
    } finally {
      setIsDownloading(false);
      setProgress(0);
    }
  }, [isDownloading, slides, slideWidth, slideHeight, bgColor, filename]);

  return (
    <>
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className={className}
        data-testid="download-pdf-btn"
        title="Download as PDF"
      >
        {isDownloading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Generating {progress}%
          </>
        ) : (
          <>
            <Download size={16} />
            Download PDF
          </>
        )}
      </button>

      {/* Hidden offscreen export container */}
      <div
        ref={exportRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-100000px",
          top: 0,
          width: `${slideWidth}px`,
          pointerEvents: "none",
          zIndex: -1
        }}
      >
        {slides.map((s, i) => (
          <div
            key={`exp-${s.id ?? i}`}
            data-export-slide
            style={{
              width: `${slideWidth}px`,
              height: `${slideHeight}px`,
              backgroundColor: bgColor,
              position: "relative",
              overflow: "hidden"
            }}
          >
            {renderSlide(s, i)}
          </div>
        ))}
      </div>
    </>
  );
}
